/**
 * Nuovo giocatore (V41): anagrafica con tesseramento immediato facoltativo.
 *
 * Aperto dalla rosa (`?ss=`) il tesseramento e gia scelto. Salvato, si
 * torna da dove si veniva, rosa o elenco giocatori. Anagrafica e
 * tesseramento sono una sola operazione: se il numero di maglia e occupato
 * il giocatore non viene creato a meta.
 */

import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ambitoDelPermesso, nuovoGiocatoreSchema } from '@calcio/shared';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { useIndietro } from '../navigazione/indietro.js';
import { erroriPerCampo, messaggio } from '../comuni/dati.js';
import { type SquadraStagione, useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { valoriCampoDa, valoriDa } from './anagrafica.js';
import { CampiAnagrafica, CampiCampo } from './CampiAnagrafica.js';

const NESSUNA = 'nessuna';

export function NuovoGiocatore() {
  const { utente, squadre, ricaricaSquadre } = useContesto();
  const [parametri] = useSearchParams();
  const daRosa = parametri.get('ss');

  const [valori, setValori] = useState(valoriDa());
  const [campo, setCampo] = useState(valoriCampoDa());
  const [ss, setSs] = useState(daRosa ?? NESSUNA);
  const [numero, setNumero] = useState('');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const torna = useIndietro(daRosa ? `/sq/${daRosa}/rosa` : '/giocatori');

  // Il guscio lascia passare chiunque veda i giocatori: creare e dell'Admin.
  if (ambitoDelPermesso(utente.assegnazioni, 'giocatore.gestisci') !== 'societa') {
    return <NonTrovata />;
  }

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = nuovoGiocatoreSchema.safeParse({
      ...valori,
      ...campo,
      tesseramento:
        ss === NESSUNA
          ? undefined
          : { squadraStagioneId: ss, numeroMaglia: numero === '' ? null : Number(numero) },
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama('/giocatori', { metodo: 'POST', corpo: esito.data });
      await ricaricaSquadre();
      toast.success('Giocatore creato.');
      torna();
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  // Stagione corrente in testa, come nel selettore: l'elenco arriva gia ordinato.
  const perStagione = new Map<string, SquadraStagione[]>();
  for (const s of squadre ?? []) {
    perStagione.set(s.stagione, [...(perStagione.get(s.stagione) ?? []), s]);
  }

  return (
    <form onSubmit={(e) => void invia(e)}>
      <Testata titolo="Nuovo giocatore" indietro="/" />
      <Corpo className="flex max-w-2xl flex-col gap-8">
        <FieldGroup>
          <CampiAnagrafica valori={valori} errori={errori} onCambia={setValori} />
          <CampiCampo valori={campo} errori={errori} onCambia={setCampo} />
        </FieldGroup>

        <FieldSet>
          <FieldLegend>Tesseramento</FieldLegend>
          <FieldDescription>
            Facoltativo: il giocatore entra subito in una rosa, da oggi.
          </FieldDescription>
          <FieldGroup>
            <div className="grid gap-4 @lg:grid-cols-[2fr_1fr]">
              <Field>
                <FieldLabel htmlFor="nuovo-squadra">Squadra</FieldLabel>
                <Select value={ss} onValueChange={setSs}>
                  <SelectTrigger id="nuovo-squadra" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={NESSUNA}>Nessuna, solo anagrafica</SelectItem>
                    </SelectGroup>
                    {[...perStagione].map(([stagione, elenco]) => (
                      <SelectGroup key={stagione}>
                        <SelectLabel>{stagione}</SelectLabel>
                        {elenco.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.squadra}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field data-invalid={Boolean(errori.tesseramento)} data-disabled={ss === NESSUNA}>
                <FieldLabel htmlFor="nuovo-numero">Numero di maglia</FieldLabel>
                <Input
                  id="nuovo-numero"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={99}
                  value={numero}
                  disabled={ss === NESSUNA}
                  aria-invalid={Boolean(errori.tesseramento)}
                  onChange={(e) => setNumero(e.target.value)}
                />
                <FieldError>{errori.tesseramento}</FieldError>
              </Field>
            </div>
          </FieldGroup>
        </FieldSet>

        {erroreServer && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{erroreServer}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={torna}>
            Annulla
          </Button>
          <PulsanteInvio type="submit" inCorso={inCorso}>
            Crea giocatore
          </PulsanteInvio>
        </div>
      </Corpo>
    </form>
  );
}
